import type React from 'react';

import { useCallback, useMemo } from 'react';

import { useAssetTemplateContext } from '~/modules/asset-template/contexts';
import { useLocationHandlers } from '~/hooks/useLocationHandlers';
import { useAssetTemplatePickerContext } from '../contexts';
import { INSTALL_GEM_PAGES } from '~/constants/thumbnail';
import { useShopifyThemeInfo } from '~/modules/shopify';
import { TEMPLATE_IMAGE } from '~/constants/template';
import { useQueryShopPlatform } from '~/modules/apps';
import { useAssignedStore } from '~/stores/assigned';
import { GEmptyState } from '~/presentation/common';
import { TemplateType } from '~/types/template';
import { useTranslation } from 'react-i18next';
import { Text } from '@shopify/polaris';

interface IAssetTemplateEmptyStateConfig {
  heading: string;
  description: string[];
  primaryAction?: {
    content: string;
    onAction: () => void;
  };
  secondaryAction?: {
    content: string;
    onAction: () => void;
  };
  image: string;
}

export const AssetTemplateEmptyState: React.FC = () => {
  const getAssignedByType = useAssignedStore((state) => state.getAssignedByType);

  const { t } = useTranslation();
  const { isInstallGemPages } = useQueryShopPlatform();
  const {
    pageTypes,
    isSelectedOnlyTabGP,
    isSelectedOnlyTabShopify,
    isGPStatusOnShopifyTab,
    handleResetFilters,
  } = useAssetTemplatePickerContext();
  const { shopifyThemeId } = useShopifyThemeInfo();
  const { handleRefetchAsset } = useAssetTemplateContext();
  const { handleOpenPageGPInNewTab, handleOpenPageShopifyInNewTab, handleOpenUrlInNewTab } =
    useLocationHandlers();

  const isProductNotFound = useMemo(() => {
    if (pageTypes?.length !== 1 || pageTypes[0] !== TemplateType.PRODUCT) return false;
    return !Object.keys(getAssignedByType(TemplateType.PRODUCT) || {}).length;
  }, [getAssignedByType, pageTypes]);

  const isCollectionNotFound = useMemo(() => {
    if (pageTypes?.length !== 1 || pageTypes[0] !== TemplateType.COLLECTION) return false;
    return !Object.keys(getAssignedByType(TemplateType.COLLECTION) || {}).length;
  }, [getAssignedByType, pageTypes]);

  const handleCreateTemplate = useCallback(() => {
    if (!isInstallGemPages || isSelectedOnlyTabShopify) {
      handleOpenPageShopifyInNewTab(`themes/${shopifyThemeId}/editor`);
      return;
    }

    handleOpenPageGPInNewTab(`app/templates/v7`);
  }, [
    shopifyThemeId,
    isInstallGemPages,
    isSelectedOnlyTabShopify,
    handleOpenPageGPInNewTab,
    handleOpenPageShopifyInNewTab,
  ]);

  const emptyStateConfig = useMemo<IAssetTemplateEmptyStateConfig>(() => {
    if (!isInstallGemPages && isSelectedOnlyTabGP) {
      return {
        heading: t('GemX works even better when tested with GemPages'),
        description: [
          t(
            'Boost your results and unlock the full potential of GemX by pairing it with GemPages! Download GemPages now to customize, test, and optimize your pages seamlessly',
          ),
        ],
        primaryAction: {
          content: t('Try GemPages for free'),
          onAction: () =>
            handleOpenUrlInNewTab(
              'https://apps.shopify.com/gempages?utm_source=gemx&utm_medium=in-pick-template&utm_campaign=integration',
            ),
        },
        secondaryAction: {
          content: t('Learn more'),
          onAction: () =>
            handleOpenUrlInNewTab(
              'https://gempages.net/?utm_source=gemx&utm_medium=in-pick-template&utm_campaign=integration',
            ),
        },
        image: INSTALL_GEM_PAGES,
      };
    }

    if (isProductNotFound) {
      return {
        heading: t('No product found'),
        description: [
          t("Your store haven't had any product yet."),
          t('Start by stocking your store with products your customers will love'),
        ],
        primaryAction: {
          content: t('Add product'),
          onAction: () => handleOpenPageShopifyInNewTab('products/new'),
        },
        secondaryAction: {
          content: t('Refresh to update'),
          onAction: handleRefetchAsset,
        },
        image: TEMPLATE_IMAGE.EMPTY_PRODUCT,
      };
    }

    if (isCollectionNotFound) {
      return {
        heading: t('No collection found'),
        description: [
          t("Your store doesn't had any collection yet."),
          t('Start by stocking your store with collections your customers will love'),
        ],
        primaryAction: {
          content: t('Add collection'),
          onAction: () => handleOpenPageShopifyInNewTab('collections/new'),
        },
        secondaryAction: {
          content: t('Refresh to update'),
          onAction: handleRefetchAsset,
        },
        image: TEMPLATE_IMAGE.EMPTY_PRODUCT,
      };
    }

    if (isGPStatusOnShopifyTab) {
      return {
        heading: t('No template found'),
        description: [t('"Draft" / "Need republishing" templates exist only in GemPages')],
        secondaryAction: {
          content: t('Reset filters'),
          onAction: handleResetFilters,
        },
        image: TEMPLATE_IMAGE.EMPTY_TEMPLATE,
      };
    }

    return {
      heading: t('No template found'),
      description: [t('Create templates to start the experiment, then refresh to view here')],
      primaryAction: {
        content: t('Create template'),
        onAction: handleCreateTemplate,
      },
      secondaryAction: {
        content: t('Refresh to update'),
        onAction: handleRefetchAsset,
      },
      image: TEMPLATE_IMAGE.EMPTY_TEMPLATE,
    };
  }, [
    t,
    isInstallGemPages,
    isSelectedOnlyTabGP,
    isProductNotFound,
    isCollectionNotFound,
    isGPStatusOnShopifyTab,
    handleCreateTemplate,
    handleResetFilters,
    handleRefetchAsset,
    handleOpenUrlInNewTab,
    handleOpenPageShopifyInNewTab,
  ]);

  return (
    <GEmptyState
      heading={emptyStateConfig.heading}
      action={emptyStateConfig.primaryAction}
      secondaryAction={emptyStateConfig.secondaryAction}
      image={emptyStateConfig.image}
    >
      {emptyStateConfig.description.map((text, index) => (
        <Text key={index} as="p">
          {text}
        </Text>
      ))}
    </GEmptyState>
  );
};
